import {
  createMatrixSupabaseClient,
  type MatrixOAuthProvider,
  parseMatrixOAuthProvider
} from "@matrix-idp/supabase-client";
import { publicRuntimeValue } from "@matrix-idp/config";

const MATTERLINE_ACCOUNT_SETUP_CALLBACK = "matrix-idp-workbench://account/setup-complete";

export const auth = createMatrixSupabaseClient({
  url: requiredPublicValue("VITE_SUPABASE_URL"),
  publishableKey: requiredPublicValue("VITE_SUPABASE_PUBLISHABLE_KEY")
}).auth;

const oneTimeOperations = new Map<string, Promise<unknown>>();

export function runOnce<T>(key: string, operation: () => Promise<T>): Promise<T> {
  const existing = oneTimeOperations.get(key);
  if (existing) return existing as Promise<T>;
  const promise = operation();
  oneTimeOperations.set(key, promise);
  void promise.then(
    () => oneTimeOperations.delete(key),
    () => oneTimeOperations.delete(key)
  );
  return promise;
}

export function safeReturnTo(value: string | null): string {
  if (!value) return "/";
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}` : "/";
  } catch {
    return "/";
  }
}

export function safeAccountSetupReturnTo(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.toString() === MATTERLINE_ACCOUNT_SETUP_CALLBACK
      ? MATTERLINE_ACCOUNT_SETUP_CALLBACK
      : null;
  } catch {
    return null;
  }
}

export function safeOpsAuthorizationUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    const supabaseUrl = new URL(requiredPublicValue("VITE_SUPABASE_URL"));
    const redirectUri = publicRuntimeValue("VITE_OPS_OAUTH_REDIRECT_URI", import.meta.env);
    const clientId = publicRuntimeValue("VITE_OPS_OAUTH_CLIENT_ID", import.meta.env);
    if (
      url.origin !== supabaseUrl.origin ||
      url.pathname !== "/auth/v1/oauth/authorize" ||
      !clientId ||
      url.searchParams.get("client_id") !== clientId ||
      !redirectUri ||
      url.searchParams.get("redirect_uri") !== redirectUri
    ) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export function completionFlow(value: string | null): "invite" | "recovery" | null {
  return value === "invite" || value === "recovery" ? value : null;
}

export function establishCompletionSession(
  code: string | null,
  tokens: { accessToken: string; refreshToken: string } | null
) {
  if (code) return auth.exchangeCodeForSession(code);
  if (tokens) return auth.establishSession(tokens.accessToken, tokens.refreshToken);
  throw new Error("Account completion credentials are required");
}

export function enterpriseSsoConfiguration():
  | { mode: "disabled" }
  | { mode: "oidc"; provider: MatrixOAuthProvider }
  | { mode: "saml"; domain: string } {
  const mode = publicRuntimeValue("VITE_ENTERPRISE_SSO_MODE", import.meta.env) || "disabled";
  if (mode === "disabled") return { mode };
  if (mode === "oidc") {
    const provider = parseMatrixOAuthProvider(
      publicRuntimeValue("VITE_ENTERPRISE_OIDC_PROVIDER", import.meta.env) || ""
    );
    return { mode, provider };
  }
  if (mode === "saml") {
    const domain = publicRuntimeValue("VITE_ENTERPRISE_SSO_DOMAIN", import.meta.env);
    if (!domain) throw new Error("VITE_ENTERPRISE_SSO_DOMAIN is required for SAML SSO");
    return { mode, domain };
  }
  throw new Error("VITE_ENTERPRISE_SSO_MODE must be disabled, oidc, or saml");
}

function requiredPublicValue(name: string): string {
  const value = publicRuntimeValue(name, import.meta.env);
  if (!value) throw new Error(`${name} is required`);
  return value;
}
